import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, usePropertyField, useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const applyCtaDef: SlotGroupDefinition = {
  label: 'Apply CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Start Your Application' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const helpCtaDef: SlotGroupDefinition = {
  label: 'Phone CTA',
  fields: {
    text: {
      label: 'Phone Text',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '',
    },
    url: {
      label: 'Phone Number',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

export function ApplyNow() {
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string || '';
  const defaultPhone = usePropertyField('property_phone') as string || '';
  const fallbackPhone = callTrackingPhone || defaultPhone;

  const heading = useContentToken('applynow.heading');
  const applyTitle = useContentToken('applynow.apply_title');
  const applyCta = useSlotGroup('applynow.apply_cta', applyCtaDef);
  const helpTitle = useContentToken('applynow.help_title');
  const helpCta = useSlotGroup('applynow.help_cta', helpCtaDef);

  const helpPhoneHref = toTelUrl(helpCta.url) || (fallbackPhone ? `tel:${fallbackPhone.replace(/\D/g, '')}` : '');
  const helpPhoneDisplay =
    formatPhone(helpCta.text) || formatPhone(helpCta.url) || formatPhone(fallbackPhone);

  useRevealStyles();
  useCtaSheenStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="an-section">
      <div ref={sectionRef} className="reveal-fade-up an-container">
        <EditableToken path="applynow.heading" as="h2" className="sr-only">
          {heading || 'Apply Now'}
        </EditableToken>
        <div className="an-grid">
          {/* Apply Now Card */}
          <div className="an-card">
            <div className="an-card-icon-wrap an-card-icon--apply">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8 13h8v2H8v-2zm0 4h5v2H8v-2z" fill="currentColor"/>
              </svg>
            </div>
            <EditableToken path="applynow.apply_title" as="h3" className="an-card-title">
              {applyTitle || 'Apply Now'}
            </EditableToken>
            <RichTextContent path="applynow.apply_description" as="p" className="an-card-desc" />
            {applyCta.url ? (
              <EditableSlotGroup groupPath="applynow.apply_cta" definition={applyCtaDef}>
                <a
                  href={applyCta.url}
                  target={applyCta._target || '_blank'}
                  rel={applyCta._target === '_self' ? undefined : 'noopener noreferrer'}
                  className="an-btn vantage-cta-sheen"
                >
                  <span className="an-btn-label relative z-10">
                    {applyCta.label || 'Start Your Application'}
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                </a>
              </EditableSlotGroup>
            ) : (
              <span className="an-btn an-btn--disabled">
                <span className="an-btn-label">Application URL Not Available</span>
              </span>
            )}
          </div>

          {/* Need Help Card */}
          <div className="an-card">
            <div className="an-card-icon-wrap an-card-icon--help">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z" fill="currentColor"/>
              </svg>
            </div>
            <EditableToken path="applynow.help_title" as="h3" className="an-card-title">
              {helpTitle || 'Need Help? Call Us'}
            </EditableToken>
            <RichTextContent path="applynow.help_description" as="p" className="an-card-desc" />
            <EditableSlotGroup groupPath="applynow.help_cta" definition={helpCtaDef}>
              <a
                href={helpPhoneHref || '#'}
                target={helpCta._target || '_self'}
                className="dniphone dniphonehref an-btn vantage-cta-sheen"
              >
                <span className="an-btn-label relative z-10">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4.64 1.33A.67.67 0 015.33 1H7a.67.67 0 01.64.47l.53 1.6a.67.67 0 01-.17.69L6.88 4.88a8 8 0 003.24 3.24l1.12-1.12a.67.67 0 01.69-.17l1.6.53A.67.67 0 0114 8v1.67a.67.67 0 01-.67.66A10 10 0 013.33 1.67.67.67 0 014 1h.64z" fill="currentColor"/></svg>
                  {helpPhoneDisplay}
                </span>
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .an-section {
          padding: 2rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .an-section { padding: 2.5rem 0 4rem; }
        }
        .an-container {
          max-width: 56rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .an-container { padding: 0 1.5rem; } }

        .an-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .an-grid {
            grid-template-columns: 1fr 1fr;
            align-items: stretch;
          }
          .an-card {
            height: 100%;
          }
          .an-card-desc {
            flex: 1 1 auto;
          }
        }

        .an-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2.5rem 2rem;
          background: #f7f7f8;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .an-card:hover {
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .an-card-icon-wrap {
          width: 56px; height: 56px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 14px;
          margin-bottom: 1.25rem;
        }
        .an-card-icon--apply {
          background: color-mix(in srgb, var(--vibe-color-secondary) 12%, transparent);
          color: var(--vibe-color-secondary);
        }
        .an-card-icon--help {
          background: color-mix(in srgb, var(--vibe-color-secondary) 12%, transparent);
          color: var(--vibe-color-secondary);
        }

        .an-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          margin: 0 0 0.75rem;
        }
        .an-card-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          margin: 0 0 1.5rem;
          max-width: 22rem;
        }

        .an-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          padding: 0.875rem 2rem;
          cursor: pointer;
        }
        .an-btn-label {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        .an-btn--disabled,
        .an-btn--disabled:hover {
          background: transparent;
          color: var(--vibe-color-text-muted);
          border: 1.5px solid var(--vibe-color-border, #e5e7eb);
          cursor: default;
          opacity: 0.7;
          transform: none;
          box-shadow: none;
        }
        .an-btn--disabled::before { display: none; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ApplyNow',
  displayName: 'Apply Now',
  tokens: {
    'applynow.heading': {
      label: 'Section Heading',
      type: 'text',
      defaultValue: 'Apply Now',
    },
    'applynow.apply_title': {
      label: 'Apply Card Title',
      type: 'text',
      defaultValue: 'Apply Now',
    },
    'applynow.apply_description': {
      label: 'Apply Card Description',
      type: 'richtext',
      defaultValue:
        '<p>Ready to call our community home? Our secure online application makes it easy to apply from anywhere. Complete your application in just a few simple steps and take the first step toward your new apartment.</p>',
    },
    'applynow.help_title': {
      label: 'Help Card Title',
      type: 'text',
      defaultValue: 'Need Help? Call Us',
    },
    'applynow.help_description': {
      label: 'Help Card Description',
      type: 'richtext',
      defaultValue:
        '<p>Have questions about the application process, availability, or move-in requirements? Our leasing team is happy to walk you through every step.</p>',
    },
  },
  slotGroups: {
    'applynow.apply_cta': applyCtaDef,
    'applynow.help_cta': helpCtaDef,
  },
};

(ApplyNow as any).contentSchema = contentSchema;

export default ApplyNow;
